import React from 'react';
import { BalanceRouteMetricCard } from '@components/RouteCards/BalanceRouteMetricCard';
import { routeToBalanceRouteCardProps } from '@components/RouteCards/utils';
import { DragIndicator } from '@mui/icons-material';
import { Checkbox } from '@mui/material';
import { IRoute } from '@sp-types/routeType';
import { IRoutePolicy, Material } from '@sp-types/index';
import { applyRoutePoliciesOrder } from '@utils/routesUtils';
import { addColor, reorderList, sortByOrder } from './MaterialDetailUtils';
import { RouteDetailsPopup } from './RouteDetailPopup';

interface Props {
  isEdit: boolean;
  routes?: IRoute[];
  formulaId: string;
  selectedRoute?: IRoute;
  setSelectedRoute: React.Dispatch<React.SetStateAction<IRoute | undefined>>;
  dragEnabled?: boolean;
  multiCheckbox?: string[];
  onCheck?: (_id: string) => void;
  isLoading: boolean;
  onOrderChange?: (_routes: IRoute[]) => void;
  routesPolicies?: IRoutePolicy[];
  hidePriceCorrelation?: boolean;
  children?: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  materialsMap: Record<string, Material>;
}
export const MaterialRoutes: React.FC<Props> = ({
  routes,
  formulaId,
  selectedRoute,
  setSelectedRoute,
  isEdit,
  dragEnabled,
  multiCheckbox,
  onCheck,
  isLoading,
  onOrderChange,
  routesPolicies,
  children,
  hidePriceCorrelation = false,
  materialsMap,
}) => {
  const [sortedRoutes, setSortedRoutes] = React.useState<IRoute[] | undefined>(
    routes?.filter(({ materials }) => !!materials?.[formulaId]),
  );
  React.useEffect(() => {
    if (routes) {
      setSortedRoutes(
        applyRoutePoliciesOrder(routes, routesPolicies || [])
          .sort(sortByOrder)
          .map(addColor),
      );
    }
  }, [routes, formulaId]);

  const [showPopup, setShowPopup] = React.useState<IRoute | null>(null);

  const [dragItem, setDragItem] = React.useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = React.useState<string | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, routeId: string) => {
    setDragItem(routeId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
    e.currentTarget.style.opacity = '0.4';
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, routeId: string) => {
    setDragOverItem(routeId);
    e.preventDefault();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1';
    if (sortedRoutes && dragItem && dragOverItem && dragItem !== dragOverItem) {
      const reorderedRoutes = reorderList(sortedRoutes, dragItem, dragOverItem).sort(sortByOrder);

      setSortedRoutes(reorderedRoutes);
      onOrderChange?.(reorderedRoutes);
    }

    setDragItem(null);
    setDragOverItem(null);
  };

  return (
    <div className="flex flex-col flex-1 h-full overflow-hidden gap-3  text-base  relative">
      <p className="flex flex-row text-lg text-red">{isEdit ? 'Edit routes' : 'Routes'}</p>
      <div className="flex flex-col flex-1 gap-3 overflow-auto">
        {isLoading || !sortedRoutes ? (
          <RoutesSkeleton length={3} />
        ) : (
          sortedRoutes.map((route, index) => (
            <div className="flex gap-2" key={index}>
              {dragEnabled && <p className="flex flex-col justify-center w-1">{index + 1}</p>}
              <div
                key={route.id}
                draggable={dragEnabled}
                onDragStart={(e) => handleDragStart(e, route.id)}
                onDragEnter={(e) => handleDragEnter(e, route.id)}
                onDragOver={(e) => handleDragOver(e)}
                onDragEnd={(e) => handleDragEnd(e)}
                className="flex flex-1 "
              >
                {dragEnabled && (
                  <div className="flex flex-col cursor-move justify-center">
                    <DragIndicator />
                  </div>
                )}

                {multiCheckbox && (
                  <div className="  ">
                    <Checkbox
                      className="p-0 pe-3"
                      onChange={() => onCheck?.(route.id)}
                      checked={multiCheckbox.includes(route.id)}
                    />
                  </div>
                )}
                <div
                  className={`bg-white flex-1 ${dragOverItem === route.id ? 'border-2  border-purple-100' : ''}`}
                  onDoubleClick={() => setShowPopup(route)}
                >
                  <BalanceRouteMetricCard
                    showMoreInfo={() => setShowPopup(route)}
                    key={index}
                    {...routeToBalanceRouteCardProps(
                      route,
                      () => setSelectedRoute(route),
                      formulaId,
                      '',
                      materialsMap[formulaId],
                      selectedRoute?.id === route.id,
                    )}
                    routeColor={route.color}
                    hidePriceCorrelation={hidePriceCorrelation}
                  >
                    {children && React.cloneElement(children, { routeId: route.routeId, route })}
                  </BalanceRouteMetricCard>
                </div>
              </div>
            </div>
          ))
        )}
        {sortedRoutes?.length === 0 && (
          <div className="flex pt-8 mx-auto*">No routes availble for selected material</div>
        )}
        {showPopup && <RouteDetailsPopup onClose={() => setShowPopup(null)} route={showPopup} />}
      </div>
    </div>
  );
};

const RoutesSkeleton: React.FC<{ length: number }> = ({ length }) => (
  <div className="flex flex-col gap-3">
    {Array.from({ length }).map((_, i) => (
      <div className="h-16 bg-gray-100 animate-pulse rounded-md" key={i}></div>
    ))}
  </div>
);
